import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import assistantIcon from '@/assets/icons/assistantIcon.svg';
import filledAssistantIcon from '@/assets/icons/filledAssistantIcon.svg';
import filledNarrativeIcon from '@/assets/icons/filledNarrativeIcon.svg';
import narrativeIcon from '@/assets/icons/narrativeIcon.svg';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { APP_HEADER_TABS_VISIBLE_PATHS } from '@/utils/constants';
import { ROUTES } from '@/utils/routeUtils';
import { cn } from '@/utils/utils';

export type TabValue = 'assistant' | 'narrative' | 'profile';

const TAB_CONFIG: Record<
  TabValue,
  {
    filledIcon?: string;
    icon?: string;
    label: string;
    labelKey: string;
    route: string;
  }
> = {
  assistant: {
    filledIcon: filledAssistantIcon,
    icon: assistantIcon,
    label: 'Assistant',
    labelKey: 'appHeader.tabs.assistant',
    route: ROUTES.ASSISTANT,
  },
  narrative: {
    filledIcon: filledNarrativeIcon,
    icon: narrativeIcon,
    label: 'Narrative',
    labelKey: 'appHeader.tabs.narrative',
    route: ROUTES.NARRATIVE,
  },
  profile: {
    filledIcon: '',
    icon: '', // not used currently
    label: 'Profile',
    labelKey: 'appHeader.tabs.profile',
    route: ROUTES.PROFILE,
  },
};

const StyledTrigger = ({ active, value }: { active: boolean; value: TabValue }) => {
  const config = TAB_CONFIG[value];
  const { t } = useTranslation();

  return (
    <TabsTrigger
      className={cn(
        'flex h-full flex-1 cursor-pointer flex-col items-center justify-center gap-1 rounded-none border-0 text-xs shadow-none transition focus:outline-none focus-visible:border-0 focus-visible:ring-0 data-active:border-0 data-active:shadow-none',
        active ? 'text-foreground' : 'text-neutral-mid',
      )}
      style={
        active && (value === 'assistant' || value === 'narrative')
          ? { borderTop: '2px solid var(--Primary-Primary-400, #4FB98F)' }
          : undefined
      }
      value={value}
    >
      <img
        alt=""
        className="icon-themed h-4 w-4 opacity-100"
        src={active ? config.filledIcon : config.icon}
      />
      <span className={cn(active && 'font-semibold')}>
        {t(config.labelKey, { defaultValue: config.label })}
      </span>
    </TabsTrigger>
  );
};

export function AppHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const shouldShowTabs = APP_HEADER_TABS_VISIBLE_PATHS.some((path) => pathname.startsWith(path));

  if (!shouldShowTabs) {
    return null;
  }

  const activeTab: TabValue = pathname.startsWith(ROUTES.NARRATIVE) ? 'narrative' : 'assistant';

  const tabs: Array<TabValue> = ['assistant', 'narrative'];

  return (
    <div className="max-w-page border-t-border bg-background flex h-14 w-full items-center border-t">
      <Tabs
        className="h-full w-full"
        onValueChange={(value) => navigate(TAB_CONFIG[value as TabValue].route)}
        value={activeTab}
      >
        <TabsList className="h-full w-full gap-0 bg-transparent p-0">
          {tabs.map((tab) => (
            <StyledTrigger active={activeTab === tab} key={tab} value={tab} />
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}
